if (!customElements.get('collection-spotlight')) {
  customElements.define('collection-spotlight', class CollectionSpotlight extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.closest('.collection-spotlight__track');
      this.links = [...this.querySelectorAll('[data-spotlight-link]')];
      this.panels = [...this.querySelectorAll('[data-spotlight-panel]')];
      this.descriptions = [...this.querySelectorAll('[data-spotlight-description]')];
      this.content = this.querySelector('.collection-spotlight__content');
      this.header = document.querySelector('.section-header');
      this.desktop = matchMedia('(min-width: 990px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.track || !this.links.length || !this.content) return;

      this.classList.add('collection-spotlight--ready');
      this.panels.concat(this.descriptions).forEach(panel => { panel.hidden = false; });
      this.show(this.links[0].dataset.spotlightLink);

      this.links.forEach(link => {
        link.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.show(link.dataset.spotlightLink);
        }, { signal });
        link.addEventListener('focus', () => this.show(link.dataset.spotlightLink), { signal });
      });
      this.addEventListener('shopify:block:select', event => this.show(event.detail.blockId), { signal });
      // Native scrolling remains available to the mouse, touchpad, keyboard and anchor links.
      window.addEventListener('scroll', () => this.queueScroll(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueLayout(), { passive: true, signal });
      window.addEventListener('load', () => this.queueLayout(), { once: true, signal });
      this.desktop.addEventListener('change', () => this.queueLayout(), { signal });
      this.reducedMotion.addEventListener('change', () => this.queueLayout(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.queueLayout());
      this.resizeObserver.observe(this.content);
      if (this.header) this.resizeObserver.observe(this.header);
      this.querySelectorAll('img').forEach(image =>
        image.addEventListener('load', () => this.queueLayout(), { signal })
      );
      document.fonts?.ready.then(() => { if (this.isConnected) this.queueLayout(); });
      this.layout();
    }

    show(id) {
      if (!this.links.some(link => link.dataset.spotlightLink === id) || this.activeId === id) return;
      this.activeId = id;
      this.links.forEach(link => {
        if (link.dataset.spotlightLink === id) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.panels.concat(this.descriptions).forEach(panel => {
        const active = (panel.dataset.spotlightPanel || panel.dataset.spotlightDescription) === id;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });
    }

    queueLayout() {
      if (this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.layout();
      });
    }

    layout() {
      const eligible = this.dataset.scrollThrough === 'true' && this.desktop.matches &&
        !this.reducedMotion.matches && !window.Shopify?.designMode && this.links.length > 1;
      const headerHeight = this.header?.getBoundingClientRect().height || 0;
      const available = window.innerHeight - headerHeight;
      const mediaHeight = this.querySelector('.collection-spotlight__images')?.getBoundingClientRect().height || 0;
      const height = Math.max(this.content.getBoundingClientRect().height + 32, mediaHeight);
      // Tall merchant content must stay readable, even with enlarged text or a short window.
      const fits = height <= available;
      this.pinned = eligible && fits;
      this.top = headerHeight + Math.max(0, (available - height) / 2);
      this.step = Math.max(1, height * (Number(this.dataset.scrollStep) || 55) / 100);
      this.track.style.setProperty('--spotlight-top', `${this.top}px`);
      this.track.style.setProperty('--spotlight-height', `${height}px`);
      this.track.style.setProperty('--spotlight-travel', `${this.step * this.links.length}px`);
      this.track.classList.toggle('collection-spotlight__track--pinned', this.pinned);
      this.classList.toggle('collection-spotlight--pinned', this.pinned);
      this.updateFromScroll();
    }

    queueScroll() {
      if (!this.pinned || this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      if (!this.pinned) return;
      const distance = this.top - this.track.getBoundingClientRect().top;
      const index = Math.max(0, Math.min(this.links.length - 1, Math.floor(distance / this.step)));
      this.show(this.links[index].dataset.spotlightLink);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.layoutFrame = this.scrollFrame = null;
    }
  });
}
